import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  concat,
  defer,
  EMPTY,
  from,
  lastValueFrom,
  of,
  throwError,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  tap,
  timeout,
} from 'rxjs/operators';
import { DeviceInfoService } from '../../../shared/services/device-info.service';
import { LoadingSpinnerService } from '../../../shared/services/loading-spinner.service';
import { ToasterService } from '../../../shared/services/toaster.service';
import { SyncClockService } from '../../../shared/services/sync-clock.service';
import { gTDB } from '../../../shared/services/db';

@Injectable({
  providedIn: 'root',
})
export class JobsService {
  constructor(
    private http: HttpClient,
    private deviceInfoService: DeviceInfoService,
    private toasterService: ToasterService,
    private spinnerService: LoadingSpinnerService,
    private clockService: SyncClockService,
  ) {}
  async updateCachedItemsInIndexDb(path: string, data: any) {
    const jobData = {
      ...data,
      shortPath: path,
      updatedAt: new Date().getTime(),
    };

    await gTDB.cachedData.put(jobData, path);
  }
  fetchJobDetails(requestData?: any, params?: any) {
    return this.http
      .post('/api/job/list_jobs/', requestData, { params: params })
      .pipe(map((response) => response));
  }
  getJobListData(requestData?: any, params?: any) {
    return this.http
      .post('/api/job/get_job_list/', requestData, { params: params })
      .pipe(map((response) => response));
  }

  getCachedAndServerItems(
    path: string,
    body: any,
    shouldPassGps = false,
    method: 'POST' | 'GET' = 'POST',
  ) {
    let isCacheFresh = false;

    const cache$ = defer(async () => {
      const result: any = await gTDB.cachedData
        .where('shortPath')
        .equals(path)
        .first();

      // Check if cache exists and was saved less than 5 seconds (5000ms) ago
      if (result && result?.updatedAt && Date.now() - result.updatedAt < 5000) {
        isCacheFresh = true;
        return { ...(result || {}), isLatest: true, shortPath: path };
      }

      return { ...(result || {}), isLatest: false, shortPath: path };
    });

    const http$ = defer(() => {
      // If the cache was fresh (< 5s), return an empty observable to skip the network call entirely
      if (isCacheFresh) {
        return EMPTY;
      }

      if (shouldPassGps) {
        return from(this.deviceInfoService.getGpsCoordinates()).pipe(
          switchMap((gps) =>
            this.http.request(method, path, {
              body:
                method === 'GET'
                  ? undefined
                  : { ...body, device_info: { gps } },
              params:
                method === 'GET'
                  ? { ...body, device_info: JSON.stringify({ gps }) }
                  : undefined,
            }),
          ),
        );
      } else {
        return this.http.request(method, path, {
          body: method === 'GET' ? undefined : body,
          params: method === 'GET' ? body : undefined,
        });
      }
    }).pipe(
      filter((response: any) => response?.status == 'success'),
      tap((response: any) => {
        this.updateCachedItemsInIndexDb(path, response);
      }),
      map((response) => ({ ...response, isLatest: true, shortPath: path })),
      catchError(async (err) => {
        const result: any = await gTDB.cachedData
          .where('shortPath')
          .equals(path)
          .first();
        return { ...(result || {}), isLatest: true, shortPath: path };
      }),
    );

    return concat(cache$, http$);
  }

  async createJob(requestData: any, params?: any) {
    const body = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return await lastValueFrom(
      this.http.post('api/job/', body, { params: params }).pipe(
        timeout(3000), // Set the timeout to 3 seconds
        map((response) => response),
        catchError((error) => {
          // Handle timeout and other errors
          if (error.name === 'TimeoutError') {
            this.spinnerService.hide();
            this.toasterService.setMessage({
              errorMessage: 'Unable to add job, please try again.',
              successMessage: '',
            });
          }
          return throwError(() => error);
        }),
      ),
    );
  }

  getJobById(jobId: any, params?: any) {
    return this.http
      .get(`api/job/${jobId}`, { params: params })
      .pipe(map((response) => response));
  }
  getJobDetailById(params: any) {
    return this.http
      .get(`api/job/get_detail/`, { params: params })
      .pipe(map((response) => response));
  }
  async updateJob(requestData: any, params?: any) {
    let data = requestData.data;

    const body = {
      ...data,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .put(requestData.url, body, { params: params })
        .pipe(map((response) => response)),
    );
  }

  getAssignees(param: any) {
    return this.http.get('api/users/get_active_users/', { params: param });
  }
  getUserList(param: any, body?: any) {
    return this.http.post('api/users/get_user_list/', body, { params: param });
  }
  addNewOption(requestData: any) {
    return this.http.get(`api/${requestData.url}`);
  }
  getSNProviderBillTo() {
    return this.http.get('api/client/smartnet/provider_bill_to/');
  }

  getDashBoardData(params: any) {
    return this.http.get(`/api/dashboard/`, { params: params });
  }

  //Smart Net job APIs.
  getSmartNetJobList() {
    const body = {};
    return this.http
      .post(`/api/smart_net/?request_data=job&request_type=list`, body)
      .pipe(map((response: any) => response));
  }
  getSmartNetJobById(id: any) {
    const body = {};
    return this.http
      .post(
        `/api/smart_net/?request_data=job&request_type=retrieve&job_id=${id}/`,
        body,
      )
      .pipe(map((response: any) => response));
  }

  async jobUpdateWoSW(body?: any, params?: any) {
    const data = {
      ...body,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .post(`/api/job/update_job_wo_sw/`, data, { params: params })
        .pipe(map((response: any) => response)),
    );
  }

  async welfareCheckIn(requestBody?: any) {
    const data = {
      updated_at: requestBody?.updated_at || this.clockService.nowMs(),
      device_info: await this.deviceInfoService.getDeviceDetail(),
      ...requestBody,
    };
    return lastValueFrom(
      this.http
        .post('api/patrol/welfare_check_in/', data)
        .pipe(map((response: any) => response)),
    );
  }
  async updateMissedEvent(requestData: any) {
    const data = {
      ...requestData,
      device_info: await this.deviceInfoService.getDeviceDetail(),
    };
    return lastValueFrom(
      this.http
        .post(`/api/patrol/update_missed_event/`, data)
        .pipe(map((response: any) => response)),
    );
  }
  getWeatherInfo(body: any, params?: any) {
    return this.http
      .post(`/api/address/get_weather_info/`, body, { params: params })
      .pipe(map((response: any) => response));
  }
  async getManageInvoiceEvents(
    body?: any,
    params?: any,
    addDeviceInfo = false,
  ) {
    if (addDeviceInfo)
      body = {
        ...body,
        device_info: await this.deviceInfoService.getDeviceDetail(),
      };
    return lastValueFrom(
      this.http
        .post('/api/job/invoice_job/', body, { params: params })
        .pipe(map((response) => response)),
    );
  }
  async getPatroulRouteSuggestions(requestData?: any, params?: any) {
    return lastValueFrom(
      this.http
        .post(`/api/patrol/get_last_unique_patrol_routes/`, requestData, {
          params: params,
        })
        .pipe(map((response: any) => response)),
    );
  }
  async sendJobMultipleReports(
    body?: any,
    params?: any,
    addDeviceInfo = false,
  ) {
    if (addDeviceInfo)
      body = {
        ...body,
        device_info: await this.deviceInfoService.getDeviceDetail(),
      };
    return lastValueFrom(
      this.http
        .post('/api/job/send_multiple_job_reports/', body, { params: params })
        .pipe(map((response) => response)),
    );
  }
  async loadSuggestedShift(body?: any, params?: any) {
    return lastValueFrom(
      this.http
        .post('/api/job/load_suggested_shift/', body, { params: params })
        .pipe(map((response) => response)),
    );
  }

  getNearestJobs(body?: any) {
    return this.getCachedAndServerItems(`/api/job/list_nearest/`, body);
  }

  getNearestCheckpointList(body?: any) {
    return this.getCachedAndServerItems(
      '/api/check_point/list_nearest/',
      body,
      true,
    );
  }

  getNearesSiteList(body?: any) {
    return this.getCachedAndServerItems('/api/site/list_nearest/', body, true);
  }
  getAllQRCheckpointList() {
    return this.getCachedAndServerItems(
      `/api/check_point/list_qr_checkpoints/`,
      {},
      false,
      'GET',
    );
  }
  getAllClientList() {
    return this.getCachedAndServerItems(
      `/api/company/list_all_companies/`,
      {},
      false,
      'GET',
    );
  }
  getAllSiteList() {
    return this.getCachedAndServerItems(
      `/api/site/list_all_sites/`,
      {},
      false,
      'GET',
    );
  }
  getHomeUserPromptList() {
    return this.getCachedAndServerItems(
      `/api/user_prompt/list_all_prompts/`,
      {},
      false,
      'GET',
    );
  }
  getJobParams() {
    return this.getCachedAndServerItems(
      `/api/job/get_options/`,
      {},
      false,
      'GET',
    );
  }
}
